import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import { AccessTime, AttachMoney, CalendarToday, Person } from '@mui/icons-material';

interface Service {
  id: string;
  name: string;
  description?: string | null;
  duration?: number | null;
  price?: number | string | null;
}

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

interface AppointmentFormProps {
  service: Service;
  selectedDate: string;
  selectedTimeSlot: TimeSlot;
  notes: string;
  specialRequirements: string;
  onNotesChange: (notes: string) => void;
  onSpecialRequirementsChange: (requirements: string) => void;
  onSubmit: () => void;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  service,
  selectedDate,
  selectedTimeSlot,
  notes,
  specialRequirements,
  onNotesChange,
  onSpecialRequirementsChange,
  onSubmit,
}) => {
  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatTimeRange = (startTime: string, endTime: string) => {
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatPrice = (price?: number | string | null) => {
    if (!price) return 'Price not set';
    // Convert to number if it's a string (API might return string instead of number)
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numericPrice)) return 'Price not set';
    return `$${numericPrice.toFixed(2)}`;
  };

  const formatDuration = (duration?: number | null) => {
    if (!duration) return 'Duration not set';
    if (duration < 60) return `${duration} minutes`;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (minutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours}h ${minutes}m`;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Appointment Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review your appointment details and add any additional information.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Appointment Summary
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Person sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Service
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {service.name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarToday sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formatDate(selectedDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AccessTime sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Time
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formatTimeRange(selectedTimeSlot.startTime, selectedTimeSlot.endTime)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoney sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Price
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formatPrice(service.price)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {service.description && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Service Description
                  </Typography>
                  <Typography variant="body2">
                    {service.description}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Chip
                  label={formatDuration(service.duration)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={formatPrice(service.price)}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Additional Information
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              placeholder="Any additional notes or information for the appointment..."
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Special Requirements (Optional)"
              placeholder="Any special requirements, medical conditions, or accessibility needs..."
              value={specialRequirements}
              onChange={(e) => onSpecialRequirementsChange(e.target.value)}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Booking Summary
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Service:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {service.name}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Duration:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDuration(service.duration)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Date & Time:</Typography>
                  <Typography variant="body2" fontWeight="medium" sx={{ textAlign: 'right' }}>
                    {formatDate(selectedDate)}
                    <br />
                    {formatTimeRange(selectedTimeSlot.startTime, selectedTimeSlot.endTime)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6" color="primary">
                  {formatPrice(service.price)}
                </Typography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={onSubmit}
                sx={{ mb: 2 }}
              >
                Continue to Payment
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                You will be charged after confirming your appointment
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AppointmentForm;
